import { csrfToken } from './csrf';

/** django-allauth's headless API for a browser on the same site: session cookies, CSRF. */
const BASE = '/_allauth/browser/v1';

export type AccountState =
  | { readonly kind: 'signedOut' }
  | { readonly kind: 'verifying'; readonly email: string | null }
  | { readonly kind: 'signedIn'; readonly email: string };

export type AccountField = 'email' | 'password' | 'code';

export interface AccountProblem {
  readonly field: AccountField | null;
  /** English, and the key of its Dutch. */
  readonly message: string;
}

export interface AllauthResponse {
  readonly status: number;
  readonly body: unknown;
}

export const UNREACHABLE = 'The city register cannot be reached. Try again in a moment.';
export const TOO_MANY = 'Too many tries. Wait a few minutes and try again.';
export const CODE_EXPIRED = 'This code has expired. Sign in again to get a new one.';

const MESSAGES: Readonly<Record<string, string>> = {
  password_too_short: 'The password needs at least 8 characters.',
  password_too_common: 'This password is too common. Choose another.',
  password_entirely_numeric: 'The password cannot be only numbers.',
  password_too_similar: 'The password is too much like the email address.',
  required: 'Fill in this field.',
  email_password_mismatch: 'The email address or password is not right.',
  incorrect_code: 'That code is not right. Check the email and try again.',
};

const FIELDS: Readonly<Record<string, AccountField>> = {
  email: 'email',
  password: 'password',
  key: 'code',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function call(method: 'GET' | 'POST' | 'DELETE', path: string, body?: object) {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = method === 'GET' ? null : csrfToken();
  if (token) headers['X-CSRFToken'] = token;
  if (body) headers['Content-Type'] = 'application/json';
  try {
    const response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : null,
    });
    const json = response.headers.get('content-type')?.includes('json');
    return { status: response.status, body: json ? ((await response.json()) as unknown) : null };
  } catch {
    return null;
  }
}

export const allauth = {
  session: () => call('GET', '/auth/session'),
  signUp: (email: string, password: string) => call('POST', '/auth/signup', { email, password }),
  signIn: (email: string, password: string) => call('POST', '/auth/login', { email, password }),
  verify: (code: string) => call('POST', '/auth/email/verify', { key: code }),
  resend: () => call('POST', '/auth/email/verify/resend'),
  signOut: () => call('DELETE', '/auth/session'),
};

/** Who the server says is here. A pending email verification keeps the address it was given. */
export function stateOf(response: AllauthResponse, email: string | null): AccountState | null {
  const { body } = response;
  const data = isRecord(body) && isRecord(body.data) ? body.data : {};
  if (response.status === 200 && isRecord(data.user) && typeof data.user.email === 'string')
    return { kind: 'signedIn', email: data.user.email };
  if (response.status !== 401) return null;
  const flows = Array.isArray(data.flows) ? data.flows : [];
  const verifying = flows.some(
    (flow) => isRecord(flow) && flow.id === 'verify_email' && flow.is_pending === true
  );
  return verifying ? { kind: 'verifying', email } : { kind: 'signedOut' };
}

/** What the visitor is told when a request was refused, next to the field it concerns. */
export function problemsOf(response: AllauthResponse | null): readonly AccountProblem[] {
  if (!response || response.status >= 500) return [{ field: null, message: UNREACHABLE }];
  if (response.status === 429) return [{ field: null, message: TOO_MANY }];
  const errors =
    isRecord(response.body) && Array.isArray(response.body.errors) ? response.body.errors : [];
  const problems = errors.filter(isRecord).map((error): AccountProblem => {
    const field = typeof error.param === 'string' ? (FIELDS[error.param] ?? null) : null;
    const code = typeof error.code === 'string' ? error.code : '';
    const message =
      code === 'invalid' && field === 'email'
        ? 'Enter a valid email address.'
        : (MESSAGES[code] ?? (typeof error.message === 'string' ? error.message : UNREACHABLE));
    return { field, message };
  });
  return problems.length > 0 ? problems : [{ field: null, message: UNREACHABLE }];
}
